/**
 * MultiSelectCombobox Component
 *
 * A thin wrapper around the shadcn/Base UI Combobox configured for searchable
 * multi-select with chips, following the shadcn "Multiple" example (anchored
 * dropdown + autoHighlight + chips input inline with the chips). Keeps a small,
 * stable API (options / selected / onChange) so the form's call sites stay
 * simple.
 *
 * Each option has a stable `value` (stored/sent) and a display `label`. The
 * combobox is driven by option objects; `itemToStringValue` ensures filtering
 * operates on the label, not the value.
 */

import { Fragment, useMemo, useCallback, type JSX } from "react"
import { cn } from "@/lib/utils"
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  ComboboxValue,
  useComboboxAnchor,
} from "@/components/ui/combobox"

/** A single selectable option. */
export interface ComboboxOption {
  /** The stable value stored in state and sent to the API. */
  value: string
  /** The human-readable label shown to the user. */
  label: string
}

/** Props for the MultiSelectCombobox. */
interface MultiSelectComboboxProps {
  /** The available options to choose from. */
  options: ComboboxOption[]
  /** The currently selected values (the `value` field of selected options). */
  selected: string[]
  /** Called with the next selected values when the selection changes. */
  onChange: (next: string[]) => void
  /** Placeholder shown in the input when nothing is selected. */
  placeholder?: string
  /** Optional id used to associate an external <label>. */
  id?: string
  /** When true, the combobox is non-interactive and visually muted. */
  disabled?: boolean
  /**
   * Optional extra classes for the scrollable options list. Use this to cap the
   * dropdown height (e.g. "max-h-60") so long lists scroll rather than growing
   * unbounded. Merged after the list's default styles so it can override them.
   */
  listClassName?: string
  /**
   * Optional extra classes for the selected-chips container (the input area
   * holding the chips). Use this to cap its height and make it scroll once the
   * selection overflows, e.g. "max-h-24 overflow-y-auto". Merged after the
   * container's default styles so it can override them.
   */
  chipsClassName?: string
  /**
   * Optional element (or ref) to portal the dropdown into. When this combobox
   * is rendered inside a modal from a different UI library (e.g. a Radix
   * Dialog), the dropdown's default body-level portal lands outside the modal's
   * DOM subtree, so the modal blocks pointer events to it (no scroll, hover, or
   * clicks). Passing the modal's content element here re-parents the dropdown
   * inside the modal so it becomes interactive again.
   */
  portalContainer?:
    | HTMLElement
    | null
    | React.RefObject<HTMLElement | null>
}

/**
 * A searchable multi-select combobox with chips.
 *
 * @param props - Component props.
 * @returns The rendered combobox.
 */
export default function MultiSelectCombobox({
  options,
  selected,
  onChange,
  placeholder = "Select…",
  id,
  disabled = false,
  listClassName,
  chipsClassName,
  portalContainer,
}: MultiSelectComboboxProps): JSX.Element {
  // Anchor the dropdown to the chips container so it matches its width and
  // position (mirrors the shadcn "Multiple" example).
  const anchor = useComboboxAnchor()

  // Map from value to option for looking up selected options by their value.
  const optionByValue = useMemo<Map<string, ComboboxOption>>(
    () => new Map(options.map((option) => [option.value, option])),
    [options]
  )

  // Convert selected values back to full option objects for the Combobox.
  const selectedOptions = useMemo<ComboboxOption[]>(
    () => selected.map((v) => optionByValue.get(v)).filter(Boolean) as ComboboxOption[],
    [selected, optionByValue]
  )

  // When selection changes, extract the value strings to pass to onChange.
  const handleValueChange = useCallback(
    (nextOptions: ComboboxOption[]) => {
      onChange(nextOptions.map((opt) => opt.value))
    },
    [onChange]
  )

  return (
    <Combobox
      items={options}
      multiple
      autoHighlight
      value={selectedOptions}
      onValueChange={handleValueChange}
      itemToStringValue={(option) => option.label}
      disabled={disabled}
    >
      <ComboboxChips ref={anchor} className={cn("w-full", chipsClassName)}>
        <ComboboxValue>
          {(selectedOpts: ComboboxOption[]) => (
            <Fragment>
              {selectedOpts.map((option) => (
                <ComboboxChip key={option.value}>
                  {option.label}
                </ComboboxChip>
              ))}
              <ComboboxChipsInput
                id={id}
                placeholder={selectedOpts.length === 0 ? placeholder : ""}
              />
            </Fragment>
          )}
        </ComboboxValue>
      </ComboboxChips>
      <ComboboxContent anchor={anchor} container={portalContainer ?? undefined}>
        <ComboboxEmpty>No items found.</ComboboxEmpty>
        <ComboboxList className={cn(listClassName)}>
          {(option: ComboboxOption) => (
            <ComboboxItem key={option.value} value={option}>
              {option.label}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  )
}
